import { useEffect, useRef } from 'react';

interface ImageComponentProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

/**
 * Global image cache to persist loaded images across editor rebuilds.
 * This prevents flashing when the editor content is rebuilt.
 */
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Global dimensions cache - stores aspect ratios even when component unmounts.
 * Key is normalized (without URL tokens) to handle token refreshes.
 */
const dimensionsCache = new Map<string, { width: number; height: number }>();

/**
 * Global DOM element cache - stores actual rendered img elements.
 * When component remounts, we can reattach the same DOM element to prevent any flash.
 * Key is the stable cache key (URL pathname without query params).
 */
const domElementCache = new Map<string, HTMLImageElement>();

/**
 * Extract a stable cache key from URL by removing query parameters.
 * This ensures the same image with different tokens shares cache.
 */
function getStableCacheKey(src: string): string {
  try {
    const url = new URL(src);
    return url.pathname; // Just the path, no query params
  } catch {
    return src;
  }
}

/**
 * Image component that maintains visibility during editor rebuilds.
 * Uses DOM element caching to completely prevent flash on rebuild.
 */
export function ImageComponent({ src, alt, width }: ImageComponentProps) {
  const cacheKey = getStableCacheKey(src);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Check if we have a cached DOM element for this image
    const cachedElement = domElementCache.get(cacheKey);
    if (cachedElement) {
      // Reuse the cached DOM element - this prevents any flash!
      // Update src in case token changed
      if (cachedElement.src !== src) {
        cachedElement.src = src;
      }
      cachedElement.alt = alt;

      // Only append if not already a child
      if (cachedElement.parentElement !== container) {
        container.innerHTML = '';
        container.appendChild(cachedElement);
      }
      return;
    }

    // No cached element - create a new one
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    img.draggable = false;
    if (width) {
      img.width = width;
    } else {
      img.style.maxWidth = '150px';
    }

    img.onload = () => {
      const loadedDims = { width: img.naturalWidth, height: img.naturalHeight };
      imageCache.set(src, img);
      dimensionsCache.set(cacheKey, loadedDims);
      domElementCache.set(cacheKey, img);
    };

    container.innerHTML = '';
    container.appendChild(img);

    // Cache immediately even before load completes (for structure)
    domElementCache.set(cacheKey, img);
  }, [src, alt, cacheKey, width]);

  return <span ref={containerRef} style={{ display: 'inline-block' }} />;
}
